/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useEffect, useState } from "react";

import AutoComplete from "../../../../components/hookForms/autoComplete";
import CloseIcon from "@mui/icons-material/Close";
import IGenericMotivoResult from "../../../../interfaces/IGenericMotivoResult";
import IGenericResult from "../../../../interfaces/IGenericResult";
import IconButton from "@mui/material/IconButton";
import InputDocumento from "../../../../components/hookForms/inputDocumento";
import InputMask from "../../../../components/hookForms/inputMask";
import InputNumber from "../../../../components/hookForms/inputNumber";
import InputText from "../../../../components/hookForms/textField";
import DatePickerMui from "../../../../components/hookForms/datePicker";
import messages from "../../../../utils/messages";
import { format, isFuture, isValid, parse, startOfDay } from "date-fns";
import serviceConcessionaria from "../../../../services/serviceConcessionaria";
import serviceMotivoMovimentacaoInstalacao from "../../../../services/serviceMotivoMovimentacaoInstalacao";
import serviceRebalanceMovimentacaoInstalacao from "../../../../services/serviceRebalanceMovimentacaoInstalacao";
import serviceUsina from "../../../../services/serviceUsina";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { isDocumentoValido, nomeTipoDocumento, sanitizeDocumento } from "../../../../utils/documento";

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    onClose?: any;
    data: {
        open: boolean;
        id: string;
        cicloFaturamento: string;
        nomeConcessionaria: string;
        tipoDocumentoCliente: string;
        numeroDocumentoCliente: string;
        nomeCliente: string;
        numeroInstalacao: string;
        cotaAtual: string;
        cotaAlterada: string;
        motivo: string;
        status: string;
        observacao: string;
        concessionariaID: string;
        rebalanceMotivoMovimentacaoInstalacaoID: string;
        rebalanceCicloMovimentacaoInstalacaoID: string;
        rebalanceMovimentacaoInstalacaoID: string;
        usinaID: string;
        tipoCiclo: string;
        dataHoraUltimaAtualizacao: string;
        dataVenda: string;
        percentualDescontoContratado: string;
    };
}

const parseDataVenda = (value: string) => {
    if (!value) return null;
    const parsed = parse(value, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? parsed : null;
};

export default function DialogEdit({ open, onClose, data, onSuccess }: IDialogProps) {
    const [isDisable, setDisable] = useState(false);
    const [usinaDataOptions, setUsinaDataOptions] = useState<IGenericResult[]>([]);
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [motivoDataOptions, setMotivoDataOptions] = useState<IGenericMotivoResult[]>([]);
    const [typeDocument, setTypeDocument] = useState("");
    const [reasonType, setReasonType] = useState("");

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            cicloFaturamento: "",
            nomeConcessionaria: "",
            tipoDocumentoCliente: "",
            numeroDocumentoCliente: "",
            nomeCliente: "",
            numeroInstalacao: "",
            cotaAtual: "",
            cotaAlterada: "",
            usinaID: "",
            motivo: "",
            status: "",
            observacao: "",
            concessionariaID: "",
            dataVenda: null as Date | null,
            percentualDescontoContratado: "",
        },
    });

    const tipoDocumentoOptions = [
        { label: "CPF", value: "CPF" },
        { label: "CNPJ", value: "CNPJ" },
    ];

    useEffect(() => {
        if (data) {
            reset({
                ...data,
                // A API passou a devolver o documento já com máscara; o input trabalha
                // com o valor limpo (e o CNPJ pode ser alfanumérico).
                numeroDocumentoCliente: sanitizeDocumento(data.numeroDocumentoCliente),
                dataVenda: parseDataVenda(data.dataVenda),
            } as any);
        }
    }, [data]);

    useEffect(() => {
        setTypeDocument(data.tipoDocumentoCliente);
    }, [data]);

    // O tipo do motivo ("I" = inclusão) define se data da venda e desconto são obrigatórios.
    useEffect(() => {
        const motivo = motivoDataOptions.find(
            (item) => String(item.value) === String(data.rebalanceMotivoMovimentacaoInstalacaoID)
        );
        setReasonType(motivo?.tipo ?? "");
    }, [data, motivoDataOptions]);

    useEffect(() => {
        setMotivoData();
    }, []);

    const setMotivoData = async () => {
        serviceMotivoMovimentacaoInstalacao
            .getMotivos()
            .then((result) => {
                setMotivoDataOptions(result?.data);
            })
            .catch(() => {
                setMotivoDataOptions([]);
            });
    };

    useEffect(() => {
        setUsinaData();
    }, []);

    const setUsinaData = async () => {
        serviceUsina
            .getUsinas()
            .then((result) => {
                setUsinaDataOptions(result?.data);
            })
            .catch(() => {
                setUsinaDataOptions([]);
            });
    };

    useEffect(() => {
        setConcessionariaData();
    }, []);

    const setConcessionariaData = async () => {
        serviceConcessionaria
            .getConcessionarias()
            .then((result) => {
                setConcessionariaDataOptions(result?.data);
            })
            .catch(() => {
                setConcessionariaDataOptions([]);
            });
    };

    function handleChange(e: any) {
        if (e && e.tipo) {
            setReasonType(e.tipo);
        }
        if (e === "CNPJ" || e === "CPF") {
            setTypeDocument(e);
        }
    }

    const validate = (values: any) => {
        // Confere o dígito verificador, inclusive do CNPJ alfanumérico. Aqui o tipo já chega como "F"/"J".
        if (
            values.tipoDocumentoCliente &&
            values.numeroDocumentoCliente &&
            !isDocumentoValido(values.tipoDocumentoCliente, values.numeroDocumentoCliente)
        ) {
            toast.error(`Número de ${nomeTipoDocumento(values.tipoDocumentoCliente)} inválido`);
            return false;
        }
        // Obrigatórios apenas para motivo de inclusão — nos demais tipos o backend aceita nulo.
        if (reasonType === "I") {
            if (!values.dataVenda) {
                toast.error("O campo DATA DA VENDA deve ser informado");
                return false;
            }
            if (values.percentualDescontoContratado === null) {
                toast.error("O campo DESCONTO CONTRATADO deve ser informado");
                return false;
            }
        }
        if (
            values.percentualDescontoContratado !== null &&
            (values.percentualDescontoContratado < 0 || values.percentualDescontoContratado > 100)
        ) {
            toast.error("O campo DESCONTO CONTRATADO deve estar entre 0 e 100");
            return false;
        }
        return true;
    };

    const onSubmit = (values: any) => {
        setDisable(true);

        if (values.dataVenda && !isValid(values.dataVenda)) {
            toast.error("O campo DATA DA VENDA está em formato inválido. Formato esperado: 'dd/mm/aaaa'");
            setDisable(false);
            return;
        }
        if (values.dataVenda && isFuture(startOfDay(values.dataVenda))) {
            toast.error("O campo DATA DA VENDA não pode ser uma data futura");
            setDisable(false);
            return;
        }

        const treatedValues = {
            ...values,
            ativo: "S",
            numeroDocumentoCliente: sanitizeDocumento(values.numeroDocumentoCliente),
            dataVenda: values.dataVenda ? format(values.dataVenda, "dd/MM/yyyy") : null,
            percentualDescontoContratado: values.percentualDescontoContratado
                ? Number(String(values.percentualDescontoContratado).replaceAll(".", "").replaceAll(",", "."))
                : null,
            tipoCiclo:
                values.tipoCiclo === "Comercial"
                    ? "C"
                    : values.tipoCiclo === "Financeiro"
                    ? "F"
                    : values.tipoCiclo === "Pós venda"
                    ? "P"
                    : values.tipoCiclo === "Pré-Faturamento"
                    ? "M"
                    : "",
            tipoDocumentoCliente:
                values.tipoDocumentoCliente === "CPF" ? "F" : values.tipoDocumentoCliente === "CNPJ" ? "J" : "",
            status: values.status === "Aberto" ? "A" : values.status === "Processado" ? "P" : "E",
            usinaID: values.usinaID ? Number(values.usinaID) : null,
        };

        if (!validate(treatedValues)) {
            setDisable(false);
            return;
        }

        serviceRebalanceMovimentacaoInstalacao
            .update(treatedValues)
            .then((result) => {
                if (result) {
                    toast.success(messages.updateSucess);
                    onClose(true);
                    onSuccess();
                    reset();
                }
            })
            .catch((err) => {
                toast.error(messages.updateError, err);
            })
            .finally(() => {
                setDisable(false);
            });
    };

    return (
        <>
            <Dialog
                maxWidth="md"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        onClose();
                    }
                }}
                open={open}
            >
                <DialogTitle>
                    Editar movimentação
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose(true);
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form>
                    <DialogContent dividers>
                        <Box>
                            <Grid container spacing={1}>
                                <Grid item md={6}>
                                    <InputMask
                                        name="cicloFaturamento"
                                        label="Ciclo"
                                        mask="99/9999"
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <AutoComplete
                                        name="concessionariaID"
                                        label="Concessionária"
                                        control={control}
                                        error={errors}
                                        options={concessionariaDataOptions}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <AutoComplete
                                        name="tipoDocumentoCliente"
                                        label="Tipo de documento"
                                        onChange={(e) => handleChange(e)}
                                        control={control}
                                        error={errors}
                                        options={tipoDocumentoOptions}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <InputDocumento
                                        name="numeroDocumentoCliente"
                                        label="Documento"
                                        maxLength={typeDocument === "CPF" ? 11 : 14}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <AutoComplete
                                        name="usinaID"
                                        label="Usina"
                                        options={usinaDataOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <InputNumber
                                        name="numeroInstalacao"
                                        label="Instalação"
                                        control={control}
                                        error={errors}
                                        decimalScale={0}
                                        noThousandSeparator
                                        maxLength={15}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <InputText
                                        name="nomeCliente"
                                        label="Nome cliente"
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <InputNumber
                                        name="cotaAtual"
                                        label="Cota atual"
                                        control={control}
                                        error={errors}
                                        decimalScale={0}
                                        noThousandSeparator
                                        maxLength={10}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <InputNumber
                                        name="cotaAlterada"
                                        label="Cota alterada"
                                        control={control}
                                        error={errors}
                                        decimalScale={0}
                                        noThousandSeparator
                                        maxLength={10}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <DatePickerMui
                                        name="dataVenda"
                                        label="Data da venda"
                                        format="dd/MM/yyyy"
                                        noDateForward
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>

                                <Grid item md={6}>
                                    <InputNumber
                                        name="percentualDescontoContratado"
                                        label="Desconto contratado (%)"
                                        control={control}
                                        error={errors}
                                        decimalScale={2}
                                        noThousandSeparator
                                        maxLength={6}
                                    />
                                </Grid>

                                <Grid item md={12}>
                                    <AutoComplete
                                        name="rebalanceMotivoMovimentacaoInstalacaoID"
                                        label="Motivo"
                                        onChange={(e) => handleChange(e)}
                                        control={control}
                                        error={errors}
                                        options={motivoDataOptions}
                                    />
                                </Grid>

                                <Grid item md={12}>
                                    <InputText name="observacao" label="Observação" control={control} error={errors} />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                        <Button
                            onClick={onClose}
                            color="warning"
                            variant="contained"
                            style={{ width: "159px", height: "44px" }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                            color="primary"
                            variant="contained"
                            disabled={isDisable}
                            style={{ width: "159px", height: "44px" }}
                        >
                            Salvar
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
